import { useSession, useUser } from '@descope/react-sdk';
import * as Sentry from '@sentry/react';
import { useEffect, useRef } from 'react';
import {
  mixpanelIdentifyUser,
  mixpanelResetIdentity,
  setAuthenticatedUser,
  trackPageview,
} from '../lib/analytics';
import { customerIOIdentify, customerIOReset } from '../lib/customerio';
import { isTelemetryEnabled } from '../lib/env';
import { getDisplayName } from '../lib/utils';

/**
 * Manages telemetry identity (Sentry, Mixpanel, GTM, Customer.io) after user authentication.
 *
 * Note: First-time login tracking is handled in LandingPage.tsx using Descope's
 * firstSeen flag from the onSuccess callback for accurate detection.
 */
export default function AuthenticatedTelemetry(): null {
  const { isAuthenticated } = useSession();
  const { user } = useUser(); //get the descope user object
  const hasTrackedPageview = useRef(false);
  // Track previous auth state to detect logout transitions
  const wasAuthenticated = useRef(false);

  useEffect(() => {
    const telemetryEnabled = isTelemetryEnabled();
    if (!telemetryEnabled) return;

    // Always track the initial pageview on mount, regardless of auth state.
    // This ensures anonymous visitors are tracked for funnel analysis.
    if (!hasTrackedPageview.current) {
      trackPageview();
      hasTrackedPageview.current = true;
    }

    if (isAuthenticated && user) {
      wasAuthenticated.current = true;

      // Extract display name from Descope user profile for personalization
      const userName = getDisplayName(user);

      // Set user context in Sentry
      Sentry.setUser({ id: user.userId, email: user.email });

      // Set authenticated user for GTM tracking
      // This ensures the unified analytics layer includes user_id, email, and name in GTM events
      setAuthenticatedUser(user.userId, user.email, userName);

      // Identify user in Mixpanel with name for email personalization
      mixpanelIdentifyUser(
        user.userId,
        user.email ? { $email: user.email } : undefined,
        userName
      );

      // Identify user in Customer.io
      customerIOIdentify(user.userId, user.email, user);
    } else {
      // Only reset if we were previously authenticated (i.e. logout)
      // This prevents resetting the anonymous ID on initial load
      if (wasAuthenticated.current) {
        Sentry.setUser(null);

        // Clear authenticated user for GTM
        setAuthenticatedUser(undefined, undefined);

        mixpanelResetIdentity();
        customerIOReset();
        wasAuthenticated.current = false;
      }
    }
  }, [isAuthenticated, user]);

  return null;
}
